import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccountMembership, APIService, CreateCustomPropertyDefinitionsInput, CustomPropertyDefinitions, UpdateCustomPropertyDefinitionsInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { AiCallOutcome } from '../ai-workspace-setup.models';
import { fetchAllPropertiesByAccountAndType } from 'app/shared/pagination/paginated-api-queries';
import { stringToUppercaseUnderscore } from '../../../../../globals/functions';

@Component({
  selector: 'ws-call-outcomes',
  templateUrl: './ws-call-outcomes.component.html',
  imports: [FormsModule, NgClass, MatButton, DragDropModule]
})
export class WsCallOutcomesComponent implements OnInit, OnChanges {
  @Input() suggestions: AiCallOutcome[];
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Output() completed = new EventEmitter<void>();
  @Output() feedbackRequested = new EventEmitter<string>();

  outcomes: AiCallOutcome[] = [];
  isSaving = false;
  existingProperty: CustomPropertyDefinitions = null;
  feedback = '';
  newOutcomeName = '';
  newOutcomeNature: 'positive' | 'negative' | 'neutral' = 'neutral';

  constructor(
    private api: APIService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.initOutcomes();
    if (!this.testMode) this.checkExistingData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions'] && this.suggestions) {
      this.initOutcomes();
    }
  }

  private initOutcomes() {
    this.outcomes = (this.suggestions || []).map(o => ({
      ...o,
      value: o.value || stringToUppercaseUnderscore(o.name),
      selected: o.selected !== false,
    }));
  }

  private async checkExistingData() {
    if (!this.activeAccount?.accountId) return;
    try {
      const properties = await fetchAllPropertiesByAccountAndType(
        this.api,
        this.activeAccount.accountId
      );
      if (properties) {
        const existing = properties.find((p: any) => p.propertyName === 'callOutcome');
        if (existing) {
          this.existingProperty = existing as CustomPropertyDefinitions;
          // Pre-fill from existing values
          if (existing.possibleValues?.length) {
            this.outcomes = existing.possibleValues.map((pv: any) => {
              const attrs = pv.otherAttributes ? JSON.parse(pv.otherAttributes) : {};
              return {
                name: attrs.outcomeTitle || pv.viewValue || pv.value,
                value: pv.value,
                nature: attrs.nature || 'neutral',
                startsNewDeal: attrs.createNewDealManual || attrs.createNewDeal || false,
                junkLead: attrs.junkLead || false,
                selected: true,
              } as AiCallOutcome;
            });
          }
        }
      }
    } catch (e) {
      console.error('Error checking existing call outcomes:', e);
    }
  }

  get positiveOutcomes() { return this.outcomes.filter(o => o.nature === 'positive'); }
  get negativeOutcomes() { return this.outcomes.filter(o => o.nature === 'negative'); }
  get neutralOutcomes() { return this.outcomes.filter(o => o.nature === 'neutral'); }

  toggleOutcome(outcome: AiCallOutcome) {
    outcome.selected = !outcome.selected;
  }

  removeOutcome(outcome: AiCallOutcome) {
    this.outcomes = this.outcomes.filter(o => o !== outcome);
  }

  addOutcome() {
    if (!this.newOutcomeName.trim()) return;
    this.outcomes.push({
      name: this.newOutcomeName.trim(),
      value: stringToUppercaseUnderscore(this.newOutcomeName.trim()),
      nature: this.newOutcomeNature,
      startsNewDeal: false,
      junkLead: false,
      selected: true,
    });
    this.newOutcomeName = '';
  }

  reorderOutcomes(event: CdkDragDrop<AiCallOutcome[]>, nature: string) {
    const subset = this.outcomes.filter(o => o.nature === nature);
    moveItemInArray(subset, event.previousIndex, event.currentIndex);
    // Rebuild outcomes array preserving order across groups
    const positive = nature === 'positive' ? subset : this.positiveOutcomes;
    const neutral = nature === 'neutral' ? subset : this.neutralOutcomes;
    const negative = nature === 'negative' ? subset : this.negativeOutcomes;
    this.outcomes = [...positive, ...neutral, ...negative];
  }

  async save() {
    if (this.isSaving) return;
    const selected = this.outcomes.filter(o => o.selected);
    if (selected.length === 0) {
      this.toastr.warning('Please select at least one call outcome');
      return;
    }

    if (this.testMode) {
      this.toastr.success('Test mode: Call outcomes saved');
      this.completed.emit();
      return;
    }

    this.isSaving = true;

    try {
      const possibleValues = selected.map(o => ({
        value: o.value || stringToUppercaseUnderscore(o.name),
        viewValue: o.name.trim(),
        otherAttributes: JSON.stringify({
          outcomeTitle: o.name.trim(),
          nature: o.nature,
          createNewDeal: false,
          createNewDealManual: o.startsNewDeal || false,
          junkLead: o.junkLead || false,
        }),
      }));

      if (this.existingProperty) {
        const input = <UpdateCustomPropertyDefinitionsInput>{
          id: this.existingProperty.id,
          accountId: this.existingProperty.accountId,
          possibleValues,
          updaedById: this.activeAccount.userId,
        };
        await this.api.UpdateCustomPropertyDefinitions(input);
      } else {
        const input = <CreateCustomPropertyDefinitionsInput>{
          accountId: this.activeAccount.accountId,
          propertyName: 'callOutcome',
          displayName: 'Call Outcome',
          description: 'Call Outcome',
          scalarType: 'Option',
          isSystemProperty: true,
          parentObjectType: 'Engagement',
          possibleValues,
          createdById: this.activeAccount.userId,
          updaedById: this.activeAccount.userId,
        };
        await this.api.CreateCustomPropertyDefinitions(input);
      }

      this.toastr.success('Call outcomes saved');
      this.completed.emit();
    } catch (e: any) {
      console.error('Error saving call outcomes:', e);
      this.toastr.error(e?.errors?.[0]?.message || 'Failed to save');
    } finally {
      this.isSaving = false;
    }
  }

  submitFeedback() {
    if (this.feedback.trim()) {
      this.feedbackRequested.emit(this.feedback.trim());
      this.feedback = '';
    }
  }
}
